<script>
  import { onMount } from 'svelte'
  
  import { getConfigsWithStatuses, getCustomConfigsWithStatuses, getPoints, callRunner } from '$services/api.js'
  import { getNotificationsContext } from 'svelte-notifications'
  
  import { Search, DatePicker, DatePickerInput, Loading } from "carbon-components-svelte"
  
  import Moment from 'moment'
  import Mousetrap from 'mousetrap'
  
  import ConfigTile from '$comps/ConfigTile.svelte'
  import PointsViewer from '$comps/PointsViewer.svelte'

  import analysis from '$/analysis.json'
  import { to_number } from 'svelte/internal';

  Mousetrap.bind('left', prevDay)
  Mousetrap.bind('right', nextDay)
  Mousetrap.bind('t', today)

  let loading = true
  let configs = [], statuses = []
  let filteredConfigs = []
  let searchValue = ''
  let selectedDate = Moment().format('YYYY-MM-DD')
  let showPointsViewer = false, viewConfig
  let workersControlState = {
    'sa': false, 'sf-00': false, 'sf-12': false,
    'ca': false, 'cf-00': false, 'cf-12': false
  }
  const { addNotification } = getNotificationsContext()

  function compare( a, b ) {
    if ( a.metadatas.name.toLowerCase() < b.metadatas.name.toLowerCase() ){
      return -1;
    }
    if ( a.metadatas.name.toLowerCase() > b.metadatas.name.toLowerCase() ){
      return 1;
    }
    return 0;
  }

  function prevDay () {
    selectedDate = Moment(selectedDate).subtract(1, 'days').format('YYYY-MM-DD')
    loadConfigs()
  }

  function nextDay () {
    if (Moment(selectedDate) < Moment().subtract(1, 'days')) selectedDate = Moment(selectedDate).add(1, 'days').format('YYYY-MM-DD')
    loadConfigs()
  }

  function today () {
    selectedDate = Moment().format('YYYY-MM-DD')
    loadConfigs()
  }
  
  async function loadConfigs () {
    loading = true
    configs = []
    filteredConfigs = []
    
    const _configs = await getConfigsWithStatuses(selectedDate)
    const _customConfigs = await getCustomConfigsWithStatuses(selectedDate)
    
    configs = configs.concat(_configs).concat(_customConfigs)
    
    loading = false
    
    filterConfigs()
  }
  
  function filterConfigs () {
    if (searchValue !== '') {
      if (['is:OK', 'is:NOK', 'is:PND', 'is:WRN'].includes(searchValue)) {
	filteredConfigs = configs.filter(c => c.status == searchValue.replace('is:', ''))
	
      } else {
	filteredConfigs = configs.filter(dc => dc.metadatas.name.includes(searchValue))
	
      }
      
    } else {
      filteredConfigs = configs
      
    }
  }

  function rerunWorker (runnerName) {
    workersControlState[runnerName] = true
    callRunner(runnerName).then(res => {
      addNotification({
	text: `Worker "${runnerName}" relaunched for ${selectedDate}`,
	type: 'success',
	position: 'bottom-right',
      })
    })
  }

  function openPointsViewer (evt) {
    viewConfig = evt.detail.config
    showPointsViewer = true
  }

  function closePointsViewer () {
    showPointsViewer = false
    viewConfig = undefined
  }
  
  $: searchValue, filterConfigs()
  // $: selectedDate, loadConfigs()
  
  onMount(() => {
    loadConfigs()
  })
</script>

<svelte:head>
  <link rel="stylesheet" href="https://unpkg.com/leaflet@1.6.0/dist/leaflet.css"
	integrity="sha512-xwE/Az9zrjBIphAcBb3F6JVqxf46+CDLwfLMHloNu6KEQCAWi6HcDUbeOfBIptF7tcCzusKFjFw2yuvEpDL9wQ=="
	crossorigin=""/>
</svelte:head>

<main id="products-page">

  {#if loading}
    <Loading />
  {/if}

  {#if showPointsViewer && viewConfig}
    <PointsViewer config={viewConfig} on:closePointsViewer={closePointsViewer} />
  {/if}

  
  <div id="page-header"><h1>CONFIGS</h1></div>
  
  <div id="controls-container">
    <div id="controls-container-search">
      <Search
        placeholder="Search product..."
        bind:value={searchValue} />
    </div>
    
    <div id="controls-container-date">
      <DatePicker
        datePickerType="single"
        dateFormat="Y-m-d"
        maxDate={Moment().format('YYYY-MM-DD')}
	on:change={loadConfigs}
        bind:value={selectedDate}>
        
        <DatePickerInput
          hideLabel
          size="xl"
          labelText=""
          placeholder="mm/dd/yyyy" />
      </DatePicker>
    </div>
  </div>

  <div id="help">
    <h1>Help</h1>

    <div id="help-content">
      <div id="help-content-colors">
	<h2>Colors</h2>
	<div class="help-content-colors-line"><span class="color-dot status-OK">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
	  <b>OK</b> Everything is fine</div>
	<div class="help-content-colors-line"><span class="color-dot status-NOK">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
	  <b>NOK</b> PANIC ! </div>
	<div class="help-content-colors-line"><span class="color-dot status-WRN">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
	  <b>WRN</b> Things will be OK in the hour, otherwise, PANIC </div>
	<div class="help-content-colors-line"><span class="color-dot status-PND">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
	  <b>PND</b> This config is not scheduled for now </div>
	<div class="help-content-colors-line"><span class="color-dot status-IGN">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
	  <b>IGN</b> This config is not monitored </div>
      </div>

      <div id="help-content-reading">
	<h2>Reading</h2>
	<p>The borders colors shows the statuses of the deliveries</p>
	<p>For forecast deliveries, the border is split in 2 parts, the "top left" corner which shows status for 00Z, and the "bottom-right" corner which shows status for 12Z.</p>
	<br/>
	<h2>Filtering</h2>
	<p>You can type the name of the config you are looking for in the search bar.</p>
	<p>You can also filter by status, with the string <code>is:&lt;STATUS&gt;</code> (for example: <code>is:NOK</code> to show failed deliveries)</p>
	<br/>
	<h2>Dates</h2>
	<p>Using the date picker on the right. you can see statuses for selected date.</p>
	<p>After selected the date, you can re-run a delivery by clicking on the button. <b>The delivery will be runned for the selected date<b></p>
      </div>
    </div>
    
  </div>    

  <div id="workers-controls">
    <h1>Workers Control</h1>

    <div id="workers-controls-content">
      <div id="workers-controls-ana">
	<h2>Ana</h2>
	<div id="workers-controls-ana-buttons">
	  <button on:click={() => rerunWorker('sa')} disabled={workersControlState['sa']}>Standard</button>
	  <button on:click={() => rerunWorker('ca')} disabled={workersControlState['ca']}>Custom</button>
	</div>
      </div>
      
      <div id="workers-controls-forecast">
	<h2>Forecasts</h2>
	<div id="workers-controls-forecast-buttons">
	  <button on:click={() => rerunWorker('sf-00')} disabled={workersControlState['sf-00']}>Standard 00Z</button>
	  <button on:click={() => rerunWorker('sf-12')} disabled={workersControlState['sf-12']}>Standard 12Z</button>
	  <button on:click={() => rerunWorker('cf-00')} disabled={workersControlState['cf-00']}>Custom 00Z</button>
	  <button on:click={() => rerunWorker('cf-12')} disabled={workersControlState['cf-12']}>Custom 12Z</button>
	</div>
      </div>
    </div>
    
  </div>

  <div id="configs">

    <h1>ANA</h1>
    <div id="ana-configs">
      <div class="col-50">
	<h2>Classics</h2>
	<div class="col-content">
	  {#each filteredConfigs.filter(c => c.metadatas.kind.includes('ana') && !c.metadatas.specific).sort(compare) as config}
	    <ConfigTile on:reload={loadConfigs} on:openPointsViewer={openPointsViewer} {config} {selectedDate} />
	  {/each}
	</div>
      </div>
      <div class="col-50">
        <h2>Specifics</h2>
	<div class="col-content">
	  {#each filteredConfigs.filter(c => c.metadatas.kind.includes('ana') && c.metadatas.specific).sort(compare) as config}
	    <ConfigTile on:reload={loadConfigs} on:openPointsViewer={openPointsViewer} {config} {selectedDate} />
	  {/each}
	</div>
      </div>
    </div>

    
    <h1>Forecast</h1>
    <div id="ana-configs">
      <div class="col-50">
	<h2>Classics</h2>
	<div class="col-content">
	  {#each filteredConfigs.filter(c => c.metadatas.kind.includes('forecast') && !c.metadatas.specific).sort(compare) as config}
	    <ConfigTile on:reload={loadConfigs} on:openPointsViewer={openPointsViewer} {config} {selectedDate} />
	  {/each}
	</div>
      </div>
      <div class="col-50">
        <h2>Specifics</h2>
	<div class="col-content">
	  {#each filteredConfigs.filter(c => c.metadatas.kind.includes('forecast') && c.metadatas.specific).sort(compare) as config}
	    <ConfigTile on:reload={loadConfigs} on:openPointsViewer={openPointsViewer} {config} {selectedDate} />
	  {/each}
	</div>
      </div>
    </div>


  </div>
</main>

<style lang="scss">
  @import "src/variables.scss";
  
  #controls-container {
    width: 80vw;
    margin-left: 10vw;
    margin-top: 5vh;
    
    display: flex;
    justify-content: space-between;
    
    #controls-container-search {
      width: 80%;
    }
  }

  #help {
    width: 80vw;
    margin-left: 10vw;
    margin-top: 5vh;

    #help-content {
      display: flex;
      justify-content: space-between;

      #help-content-colors, #help-content-reading {
	width: 48%;
      }

      h2 { margin-bottom: 10px; }

      .help-content-colors-line {
	margin-bottom: 10px;
	
	.color-dot {
	  font-size: .8em;
	  height: 10px;
	  width: 50px;
	  margin-bottom: 50px;

	  &.status-OK { border: 2px solid $success; }
	  &.status-NOK { border: 2px solid $error; }
	  &.status-PND { border: 2px solid $info; }
	  &.status-WRN { border: 2px solid $warning; }
	  &.status-IGN {border: 2px solid $accent2; }
	}
      }    
    }
  }

  #workers-controls {
    width: 80vw;
    margin-left: 10vw;
    margin-top: 5vh;

    #workers-controls-content {
      display: flex;
      justify-content: space-between;

      #workers-controls-ana, #workers-controls-forecast {
	width: 48%;

	#workers-controls-ana-buttons {
	  display: flex;
	  justify-content: space-between;

	  button {
	    width: 49% !important;
	  }
	}

	#workers-controls-forecast-buttons {
	  display: flex;
	  justify-content: space-between;

	  button {
	    width: 24% !important;
	  }
	}
      }
    }
  }
  
  #configs {
    width: 80vw;
    margin-left: 10vw;
    margin-top: 5vh;

    #ana-configs, #forecast-configs {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;

      
      @media (max-width: 1000px) {
	flex-direction: column;
      }

      h2 {
	margin-bottom: 10px;
      }
    }
    
    .col-50 {
      width: 48%;
      
      @media (max-width: 1000px) {
	width: 100%;
      }
      
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;

      .col-content {
	width: 100%;
	display: flex;
	justify-content: space-between;
	flex-wrap: wrap;
      }
    }
  }

  button:disabled, button[disabled] {
    position: relative;
    
    &:before{
      position: absolute;
      content: "";
      top: 0;
      left: 0;
      width: 0%;
      height: 100%;
      background: $gray7;
      transition: all .35s;
    }

    &:before {
      animation: 2s disabled-button ease-in-out infinite;
    }
  }

  @keyframes disabled-button {
    0% {
      left: 0%;
      width: 0%;
    }
    50% {
      left: 0%;
      width: 100%;
    }
    100% {
      left: 100%;
      width: 0%;
    }
  }
</style>
